import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { FOUNDERS } from '@/content/social-proof';
import { track } from '@/lib/analytics';
import {
  bookCall,
  CallRequestError,
  dayKey,
  fetchCallSlots,
  POSTING_FREQUENCIES,
  requestCallback,
  visitorTimeZone,
  type CallError,
  type CallSlots,
  type PostingFrequency,
} from '@/lib/call';
import { useLang, useLangHref, useT } from '@/lib/useLang';

type Booked = { startAt: string; email: string };
type View = 'pick' | 'details' | 'booked' | 'callback' | 'callback-sent';

/**
 * Parler à Stephen avant de payer : la seconde porte d'entrée d'une agence.
 *
 * Les créneaux viennent de l'API, déjà débarrassés de ceux qui sont réservés
 * — aucun créneau n'est masqué pour paraître demandé. Ils sont lus
 * après le rendu (jamais en SSR) et affichés dans le fuseau du visiteur.
 *
 * Quand les créneaux ne sont pas lisibles, ou qu'aucun ne convient, la page
 * propose d'être rappelé : il y a toujours une suite, jamais une impasse.
 */
export function Call() {
  const t = useT();
  const lang = useLang();
  const withLang = useLangHref();
  const host = FOUNDERS[0];

  const [slots, setSlots] = useState<CallSlots | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [view, setView] = useState<View>('pick');
  const [day, setDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [booked, setBooked] = useState<Booked | null>(null);
  const [error, setError] = useState<CallError | null>(null);
  const [timeZone, setTimeZone] = useState('');

  const loadSlots = () => {
    fetchCallSlots()
      .then((value) => {
        setSlots(value);
        if (!value.bookable || value.slots.length === 0) setView((current) => (current === 'pick' ? 'callback' : current));
      })
      .catch(() => {
        setLoadFailed(true);
        setView((current) => (current === 'pick' ? 'callback' : current));
      });
  };

  useEffect(() => {
    setTimeZone(visitorTimeZone());
    loadSlots();
    track('call_page_viewed', { lang });
    // Une seule lecture à l'arrivée ; `lang` ne change pas sans navigation.
  }, []);

  const days = useMemo(() => {
    if (!slots || !timeZone) return [];
    const byDay = new Map<string, string[]>();
    for (const iso of slots.slots) {
      const key = dayKey(iso, timeZone);
      byDay.set(key, [...(byDay.get(key) ?? []), iso]);
    }
    return [...byDay.entries()].map(([key, times]) => ({ key, times }));
  }, [slots, timeZone]);

  const activeDay = days.find((entry) => entry.key === day) ?? days[0] ?? null;
  const locale = lang === 'fr' ? 'fr-FR' : 'en-US';
  const formatDay = (iso: string, style: 'short' | 'long') =>
    new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: style === 'short' ? 'short' : 'long',
      day: 'numeric',
      month: style === 'short' ? 'short' : 'long',
    }).format(new Date(iso));
  const formatTime = (iso: string) =>
    new Intl.DateTimeFormat(locale, { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));

  const chooseSlot = (iso: string) => {
    setSlot(iso);
    setError(null);
    setView('details');
    track('call_slot_selected', { lang });
  };

  const openCallback = () => {
    setError(null);
    setView('callback');
    track('call_callback_opened', { lang });
  };

  return (
    <section className="nz-page nz-page--wide nz-call-page">
      <aside className="nz-call-page__host">
        <picture>
          <source srcSet={host.photo.webp} type="image/webp" />
          <img className="nz-call-page__face" src={host.photo.fallback} alt={host.name} width={88} height={88} />
        </picture>
        <div className="nz-eyebrow">{t.callEyebrow}</div>
        <h1 className="nz-page__title">{t.callTitle}</h1>
        <p className="nz-lead">{t.callLead}</p>
        <ul className="nz-call-page__meta">
          <li>
            <Icon name="clock" className="nz-call-page__meta-icon" />
            {t.callDuration}
          </li>
          <li>
            <Icon name="users" className="nz-call-page__meta-icon" />
            {host.firstName}, {host.role[lang]}
          </li>
        </ul>
      </aside>

      <div className="nz-call-page__panel">
        {view === 'pick' && (
          <div>
            <h2 className="nz-call-page__h2">{t.callPickTitle}</h2>
            {!slots && !loadFailed ? (
              <p className="nz-call-page__muted">{t.callLoading}</p>
            ) : (
              <>
                <div className="nz-call-page__days" role="tablist" aria-label={t.callPickTitle}>
                  {days.map((entry) => (
                    <button
                      key={entry.key}
                      type="button"
                      role="tab"
                      aria-selected={entry.key === activeDay?.key}
                      className={`nz-call-page__day${entry.key === activeDay?.key ? ' is-active' : ''}`}
                      onClick={() => setDay(entry.key)}
                    >
                      {formatDay(entry.times[0], 'short')}
                    </button>
                  ))}
                </div>
                {activeDay && (
                  <div className="nz-call-page__times">
                    {activeDay.times.map((iso) => (
                      <button key={iso} type="button" className="nz-call-page__time" onClick={() => chooseSlot(iso)}>
                        {formatTime(iso)}
                      </button>
                    ))}
                  </div>
                )}
                {timeZone && (
                  <p className="nz-call-page__muted">
                    {t.callTimeZone} {timeZone.replace(/_/g, ' ')}
                  </p>
                )}
              </>
            )}
            <button type="button" className="nz-call-page__link" onClick={openCallback}>
              {t.callNoTimeWorks}
            </button>
          </div>
        )}

        {view === 'details' && slot && (
          <BookForm
            slot={slot}
            slotLabel={`${formatDay(slot, 'long')} · ${formatTime(slot)}`}
            error={error}
            onBack={() => {
              setView('pick');
              setError(null);
            }}
            onBooked={(result) => {
              setBooked(result);
              setView('booked');
            }}
            onError={(kind) => {
              setError(kind);
              if (kind === 'taken') {
                // Le créneau vient de partir : on relit les créneaux et on ramène au choix.
                setView('pick');
                setSlot(null);
                loadSlots();
              }
            }}
          />
        )}

        {view === 'booked' && booked && (
          <div className="nz-call-page__done">
            <Icon name="check" className="nz-call-page__done-icon" />
            <h2 className="nz-call-page__h2">{t.callBookedTitle}</h2>
            <p className="nz-call-page__slot">
              {formatDay(booked.startAt, 'long')} · {formatTime(booked.startAt)}
            </p>
            <p className="nz-lead">
              {t.callBookedLead} <b>{booked.email}</b>.
            </p>
          </div>
        )}

        {view === 'callback' && (
          <CallbackForm
            intro={slots?.bookable && slots.slots.length > 0 ? t.callCallbackLead : t.callCallbackOnlyLead}
            canGoBack={Boolean(slots?.bookable && slots.slots.length > 0)}
            onBack={() => setView('pick')}
            onSent={() => setView('callback-sent')}
          />
        )}

        {view === 'callback-sent' && (
          <div className="nz-call-page__done">
            <Icon name="check" className="nz-call-page__done-icon" />
            <h2 className="nz-call-page__h2">{t.callCallbackSentTitle}</h2>
            <p className="nz-lead">{t.callCallbackSentLead}</p>
          </div>
        )}

        {/* Sur le rappel aussi : si c'était le dernier créneau, la relecture bascule ici et le visiteur doit savoir pourquoi. */}
        {error === 'taken' && (view === 'pick' || view === 'callback') && (
          <p className="nz-call-page__error">{t.callErrorTaken}</p>
        )}
      </div>

      <div className="nz-call-page__more">
        <div className="nz-call-page__covers">
          <b>{t.callCoversTitle}</b>
          <ul>
            {t.callCovers.map((item) => (
              <li key={item}>
                <Icon name="check" className="nz-call-page__check" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <p className="nz-call-page__skip">
          {t.callSkip} <Link to={withLang('/')}>{t.callSkipLink}</Link>
        </p>
      </div>
    </section>
  );
}

/** Mesure le temps de remplissage, lu par la garde anti-spam de l'API. */
function useFormClock() {
  const startedAt = useRef(Date.now());
  return () => Date.now() - startedAt.current;
}

function BookForm(props: {
  slot: string;
  slotLabel: string;
  error: CallError | null;
  onBack: () => void;
  onBooked: (result: Booked) => void;
  onError: (kind: CallError) => void;
}) {
  const t = useT();
  const lang = useLang();
  const elapsed = useFormClock();
  const [sending, setSending] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const field = (name: string) => String(data.get(name) ?? '').trim();
    setSending(true);
    try {
      const result = await bookCall({
        name: field('name'),
        email: field('email'),
        startAt: props.slot,
        linkedInUrl: field('linkedInUrl'),
        postingFrequency: field('postingFrequency') as PostingFrequency | '',
        note: field('note'),
        lang,
        website: field('website'),
        elapsedMs: elapsed(),
      });
      track('call_booked', { lang });
      props.onBooked({ ...result, email: field('email') });
    } catch (error) {
      const kind = error instanceof CallRequestError ? error.kind : 'failed';
      track('call_booking_failed', { lang, kind });
      props.onError(kind);
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="nz-call-form" onSubmit={submit}>
      <button type="button" className="nz-call-page__link nz-call-page__back" onClick={props.onBack}>
        ← {t.callChangeTime}
      </button>
      <p className="nz-call-page__slot">{props.slotLabel}</p>

      <label className="nz-call-form__field">
        <span>{t.callName}</span>
        <input className="nz-form__input" name="name" autoComplete="name" required maxLength={120} />
      </label>
      <label className="nz-call-form__field">
        <span>{t.callEmail}</span>
        <input className="nz-form__input" name="email" type="email" autoComplete="email" required maxLength={254} />
      </label>
      <label className="nz-call-form__field">
        <span>
          {t.callLinkedIn} <small>{t.callOptional}</small>
        </span>
        <input className="nz-form__input" name="linkedInUrl" placeholder="linkedin.com/in/…" maxLength={300} />
      </label>
      <label className="nz-call-form__field">
        <span>
          {t.callFrequency} <small>{t.callOptional}</small>
        </span>
        <select className="nz-form__input" name="postingFrequency" defaultValue="">
          <option value="">—</option>
          {POSTING_FREQUENCIES.map((value) => (
            <option key={value} value={value}>
              {t.callFrequencyOptions[value]}
            </option>
          ))}
        </select>
      </label>
      <label className="nz-call-form__field">
        <span>
          {t.callNote} <small>{t.callOptional}</small>
        </span>
        <textarea className="nz-form__input" name="note" rows={3} maxLength={1000} placeholder={t.callNotePlaceholder} />
      </label>
      <Honeypot />

      {props.error && props.error !== 'taken' && (
        <p className="nz-call-page__error">{props.error === 'invalid' ? t.callErrorInvalid : t.callErrorFailed}</p>
      )}
      <button className="nz-form__submit" type="submit" disabled={sending}>
        {sending ? t.callSending : t.callBookSubmit}
      </button>
    </form>
  );
}

function CallbackForm(props: { intro: string; canGoBack: boolean; onBack: () => void; onSent: () => void }) {
  const t = useT();
  const lang = useLang();
  const elapsed = useFormClock();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<CallError | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const field = (name: string) => String(data.get(name) ?? '').trim();
    setSending(true);
    setError(null);
    try {
      await requestCallback({
        name: field('name'),
        phone: field('phone'),
        email: field('email'),
        note: field('note'),
        lang,
        website: field('website'),
        elapsedMs: elapsed(),
      });
      track('call_callback_requested', { lang });
      props.onSent();
    } catch (failure) {
      const kind = failure instanceof CallRequestError ? failure.kind : 'failed';
      track('call_callback_failed', { lang, kind });
      setError(kind);
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="nz-call-form" onSubmit={submit}>
      {props.canGoBack && (
        <button type="button" className="nz-call-page__link nz-call-page__back" onClick={props.onBack}>
          ← {t.callBackToTimes}
        </button>
      )}
      <h2 className="nz-call-page__h2">{t.callCallbackTitle}</h2>
      <p className="nz-call-page__muted">{props.intro}</p>

      <label className="nz-call-form__field">
        <span>{t.callName}</span>
        <input className="nz-form__input" name="name" autoComplete="name" required maxLength={120} />
      </label>
      <label className="nz-call-form__field">
        <span>{t.callPhone}</span>
        <input
          className="nz-form__input"
          name="phone"
          type="tel"
          autoComplete="tel"
          required
          minLength={6}
          maxLength={30}
          placeholder="+1 555 123 4567"
        />
      </label>
      <label className="nz-call-form__field">
        <span>
          {t.callEmail} <small>{t.callOptional}</small>
        </span>
        <input className="nz-form__input" name="email" type="email" autoComplete="email" maxLength={254} />
      </label>
      <label className="nz-call-form__field">
        <span>
          {t.callNote} <small>{t.callOptional}</small>
        </span>
        <textarea className="nz-form__input" name="note" rows={3} maxLength={1000} placeholder={t.callCallbackNotePlaceholder} />
      </label>
      <Honeypot />

      {error && <p className="nz-call-page__error">{error === 'invalid' ? t.callErrorInvalid : t.callErrorFailed}</p>}
      <button className="nz-form__submit" type="submit" disabled={sending}>
        {sending ? t.callSending : t.callCallbackSubmit}
      </button>
    </form>
  );
}

/** Invisible pour un humain, rempli par les robots de formulaire. */
function Honeypot() {
  return (
    <div className="nz-call-form__trap" aria-hidden="true">
      <label>
        Website
        <input name="website" tabIndex={-1} autoComplete="off" />
      </label>
    </div>
  );
}
